import React, { useCallback, useEffect, useState } from 'react';
import { adminApi, ProviderStatementItem } from '../api/admin';
import { providersApi, Provider } from '../api/providers';

function ReconciliationPage() {
  const [providers, setProviders] = useState<Provider[]>([]);
  const [items, setItems] = useState<ProviderStatementItem[]>([]);
  const [providerId, setProviderId] = useState('');
  const [status, setStatus] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');

  const loadItems = useCallback(async () => {
    try {
      setLoading(true);
      setItems((await adminApi.getProviderStatementItems(providerId || undefined, status || undefined)).data || []);
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to load statement items');
    } finally {
      setLoading(false);
    }
  }, [providerId, status]);

  useEffect(() => {
    void providersApi.getAll().then(response => setProviders(response || [])).catch(() => setProviders([]));
  }, []);

  useEffect(() => {
    void loadItems();
  }, [loadItems]);

  const importStatement = async () => {
    if (!providerId || !file) {
      setMessage('Select provider and CSV file first.');
      return;
    }
    try {
      setLoading(true);
      const result = (await adminApi.importProviderStatement(providerId, file)).data;
      setMessage(`Imported: ${result.imported}. Duplicates skipped: ${result.duplicates}.${result.errors.length ? ` Errors: ${result.errors.join(' ')}` : ''}`);
      setFile(null);
      await loadItems();
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to import statement');
    } finally {
      setLoading(false);
    }
  };

  const matchItem = async (item: ProviderStatementItem) => {
    const paymentId = window.prompt('Outgoing payment ID');
    if (!paymentId?.trim()) return;
    try {
      setLoading(true);
      await adminApi.matchProviderStatementItem(item.id, paymentId.trim());
      await loadItems();
    } catch (err: any) {
      setMessage(err.response?.data?.message || err.message || 'Failed to match statement item');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <h1 className="text-2xl font-semibold text-gray-900">Provider Reconciliation</h1>
      <p className="mt-1 text-sm text-gray-600">Import provider CSV statements and reconcile them with outgoing payments.</p>
      {message && <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-900">{message}</div>}
      <div className="mt-6 flex flex-wrap gap-3 rounded-lg border border-gray-200 bg-white p-4 shadow-sm">
        <select value={providerId} onChange={e => setProviderId(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">Select provider</option>
          {providers.map(provider => <option key={provider.id} value={provider.id}>{provider.name}</option>)}
        </select>
        <input type="file" accept=".csv,text/csv" onChange={e => setFile(e.target.files?.[0] || null)} className="text-sm" />
        <button onClick={() => void importStatement()} disabled={loading} className="rounded-lg bg-blue-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-50">Import CSV</button>
        <select value={status} onChange={e => setStatus(e.target.value)} className="rounded-lg border border-gray-300 px-3 py-2 text-sm">
          <option value="">All statuses</option>
          <option value="UNMATCHED">Unmatched</option>
          <option value="MATCHED">Matched</option>
        </select>
      </div>
      <div className="mt-6 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <table className="w-full">
          <thead className="bg-gray-50"><tr><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Date</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Reference</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">IBAN</th><th className="px-4 py-3 text-right text-xs uppercase text-gray-500">Amount</th><th className="px-4 py-3 text-left text-xs uppercase text-gray-500">Status</th><th className="px-4 py-3" /></tr></thead>
          <tbody className="divide-y divide-gray-200">
            {items.map(item => <tr key={item.id}><td className="px-4 py-3 text-sm">{new Date(item.statementDate).toLocaleDateString()}</td><td className="px-4 py-3 text-sm">{item.providerReference || '—'}</td><td className="px-4 py-3 font-mono text-sm">{item.iban || '—'}</td><td className="px-4 py-3 text-right text-sm">{item.amount.toFixed(2)} {item.currencyId}</td><td className="px-4 py-3 text-sm">{item.matchStatus}</td><td className="px-4 py-3 text-right">{item.matchStatus === 'UNMATCHED' && <button onClick={() => void matchItem(item)} className="text-sm font-medium text-blue-700">Match</button>}</td></tr>)}
            {!loading && items.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">No statement items</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}

export default ReconciliationPage;
